import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { GridLayout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, Check, Plus, X, RotateCcw, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WIDGETS, WIDGET_MAP, DEFAULT_LAYOUT, DEFAULT_WIDGETS, DEFAULT_PERIOD } from "../components/dashboard/widgets";
import { VELORY_LAYOUT, VELORY_HEIGHT, LAYOUT_VERSION } from "../components/dashboard/velory";
import "../components/dashboard/velory.css";

// One-time upgrade of saved layouts: prepend the Assets-overview row.
function upgradeLayout(layout, widgets, periods) {
  if ((periods || {})._layout_v === LAYOUT_VERSION) return null;
  const ids = new Set(VELORY_LAYOUT.map((l) => l.i));
  const rest = layout.filter((l) => !ids.has(l.i)).map((l) => ({ ...l, y: l.y + VELORY_HEIGHT }));
  return {
    layout: [...VELORY_LAYOUT, ...rest],
    widgets: [...VELORY_LAYOUT.map((l) => l.i), ...widgets.filter((w) => !ids.has(w))],
    periods: { ...(periods || {}), _layout_v: LAYOUT_VERSION },
  };
}

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(1200);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = ["admin", "manager"].includes(user?.role);
  const [sources, setSources] = useState({});
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [active, setActive] = useState(DEFAULT_WIDGETS);
  const [periods, setPeriods] = useState({});
  const [editing, setEditing] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch each data source once; widgets read from the shared object.
  useEffect(() => {
    const get = (url, key) => api.get(url).then(({ data }) => setSources((s) => ({ ...s, [key]: data }))).catch(() => {});
    get("/opex/summary", "opex");
    get("/analytics/portfolio-irr", "irr");
    get("/assets/report", "assets");
    get("/opex/drift-history", "drift");
    get("/pm/dashboard", "pipeline");
    get("/dashboard/tasks-due", "tasks");
    get("/dashboard/signoffs", "signoffs");
    get("/maintenance/upcoming?days=60", "maint");
    get("/opex/alerts", "alertsFeed");
    get("/dashboard/docs-expiring", "docs");
    get("/dashboard/site-health?months=12", "health");
    get("/dashboard/assets-overview", "overview");
  }, []);

  // Load saved layout (fall back to default).
  useEffect(() => {
    api.get("/dashboard/layout").then(({ data }) => {
      if (data.layout && data.layout.length) {
        let valid = data.layout.filter((l) => WIDGET_MAP[l.i]);
        let ids = (data.widgets || valid.map((l) => l.i)).filter((id) => WIDGET_MAP[id]);
        let per = data.periods || {};
        const up = upgradeLayout(valid, ids, per);
        if (up) {
          ({ layout: valid, widgets: ids, periods: per } = up);
          api.put("/dashboard/layout", up).catch(() => {});
        }
        setLayout(valid);
        setActive(ids);
        setPeriods(per);
      } else setPeriods({ ...(data.periods || {}), _layout_v: LAYOUT_VERSION });
    }).catch(() => {});
  }, []);

  const save = useCallback(async () => {
    try {
      await api.put("/dashboard/layout", { layout, widgets: active, periods });
      toast.success("Dashboard saved");
      setEditing(false);
    } catch { toast.error("Save failed"); }
  }, [layout, active, periods]);

  const reset = async () => {
    try { await api.delete("/dashboard/layout"); } catch {}
    setLayout(DEFAULT_LAYOUT); setActive(DEFAULT_WIDGETS); setPeriods({ _layout_v: LAYOUT_VERSION });
    toast.success("Reset to default");
  };

  const publishDefault = async () => {
    if (!window.confirm("Publish this layout as the default dashboard for all new users?")) return;
    try {
      await api.put("/dashboard/default", { layout, widgets: active, periods });
      toast.success("Published as team default");
    } catch { toast.error("Publish failed (admin/manager only)"); }
  };

  const setWidgetPeriod = useCallback((id, p) => {
    setPeriods((prev) => ({ ...prev, [id]: p }));
    // Persist period changes immediately when not in edit mode.
    setLayout((l) => { api.put("/dashboard/layout", { layout: l, widgets: active, periods: { ...periods, [id]: p } }).catch(() => {}); return l; });
  }, [active, periods]);

  const addWidget = (id) => {
    if (active.includes(id)) return;
    const w = WIDGET_MAP[id];
    const maxY = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    setLayout((l) => [...l, { i: id, x: 0, y: maxY, w: w.w, h: w.h }]);
    setActive((a) => [...a, id]);
    setPaletteOpen(false);
  };
  const removeWidget = (id) => {
    setLayout((l) => l.filter((x) => x.i !== id));
    setActive((a) => a.filter((x) => x !== id));
  };

  const available = WIDGETS.filter((w) => !active.includes(w.id));
  // Widgets that size themselves to their content (no inner scroll) report a pixel height here.
  const [autoH, setAutoH] = useState({});
  const fitHeight = useCallback((id, px) => {
    const h = Math.max(2, Math.ceil((px + 16) / 80));
    setAutoH((m) => (m[id] === h ? m : { ...m, [id]: h }));
  }, []);
  const visibleLayout = useMemo(
    () => layout.filter((l) => active.includes(l.i)).map((l) => (!editing && autoH[l.i] ? { ...l, h: autoH[l.i] } : l)),
    [layout, active, autoH, editing]);
  const [gridRef, gridWidth] = useContainerWidth();

  const renderCard = (id, idx = 0) => {
    const w = WIDGET_MAP[id];
    if (!w) return null;
    const Render = w.Render;
    return (
      <div className={`swiss-card ${w.bare ? "vl-bare" : "p-4"} ${editing ? "vl-editing" : ""} h-full relative overflow-hidden widget-enter transition-shadow duration-200`}
        style={{ animationDelay: `${Math.min(idx, 12) * 45}ms` }} data-testid={`widget-${id}`}>
        {editing && (
          <button onClick={() => removeWidget(id)} className="absolute top-2 right-2 z-10 btn-ghost p-1 text-[#C0392B]"
            data-testid={`widget-remove-${id}`} onMouseDown={(e) => e.stopPropagation()}><X size={14} /></button>
        )}
        <div className={editing ? "pointer-events-none select-none h-full" : "h-full"}>
          <Render sources={sources} period={periods[id] || DEFAULT_PERIOD} setPeriod={(p) => setWidgetPeriod(id, p)} fitHeight={(px) => fitHeight(id, px)} />
        </div>
      </div>
    );
  };

  return (
    <div className="velory-dash" data-testid="dashboard-page">
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <style>{`
        @keyframes widgetEnter { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .widget-enter { animation: widgetEnter 0.45s cubic-bezier(0.22,1,0.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .widget-enter { animation: none; } }
      `}</style>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label mb-2">Dashboard</div>
          <h1 className="vl-title" data-testid="dashboard-title">Assets overview</h1>
          <p className="vl-subtitle">
            {editing ? "Drag to move, drag the corner to resize, remove with ✕, or add widgets — then save." : "Ownership, income and lifecycle for every site you own or manage."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {editing && !isMobile && (
            <>
              <button onClick={() => setPaletteOpen((o) => !o)} className="btn-ghost" data-testid="dashboard-add"><Plus size={15} /> Add widget</button>
              <button onClick={reset} className="btn-ghost" data-testid="dashboard-reset"><RotateCcw size={14} /> Reset</button>
              {isAdmin && (
                <button onClick={publishDefault} className="btn-ghost" data-testid="dashboard-publish-default"><Share2 size={14} /> Set as team default</button>
              )}
              <button onClick={save} className="btn-primary" data-testid="dashboard-save"><Check size={15} /> Save</button>
            </>
          )}
          {!editing && !isMobile && (
            <button onClick={() => setEditing(true)} className="btn-ghost" data-testid="dashboard-edit"><SlidersHorizontal size={14} /> Customise</button>
          )}
          {!editing && ["admin", "manager"].includes(user?.role) && (
            <button onClick={() => nav("/projects/new")} className="btn-accent" data-testid="dashboard-add-site"><Plus size={15} /> Add site</button>
          )}
        </div>
      </div>

      {editing && paletteOpen && !isMobile && (
        <div className="swiss-card p-4 mb-5" data-testid="dashboard-palette">
          <div className="label mb-3">Add a widget</div>
          {available.length === 0 ? <div className="muted text-sm">All widgets are on your dashboard.</div> : (
            <div className="flex flex-wrap gap-2">
              {available.map((w) => (
                <button key={w.id} onClick={() => addWidget(w.id)} className="btn-ghost text-xs border border-[var(--line)]" data-testid={`palette-${w.id}`}>
                  <Plus size={12} /> {w.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isMobile ? (
        <div className="space-y-3" data-testid="dashboard-mobile">
          {active.map((id, i) => {
            const h = (WIDGET_MAP[id]?.h || 2);
            return <div key={id} className="flex flex-col [&>*]:flex-1" style={{ minHeight: Math.max(130, h * 64 + (h - 1) * 16) }}>{renderCard(id, i)}</div>;
          })}
        </div>
      ) : (
        <div ref={gridRef}>
          <GridLayout
            className={editing ? "dashboard-editing" : ""}
            width={gridWidth}
            layout={visibleLayout}
            gridConfig={{ cols: 12, rowHeight: 64, margin: [16, 16], containerPadding: [0, 0] }}
            dragConfig={{ enabled: editing, cancel: "[data-testid^='widget-remove-']" }}
            resizeConfig={{ enabled: editing }}
            onLayoutChange={(l) => editing && setLayout(l)}
          >
            {active.map((id, i) => (
              <div key={id} className={editing ? "cursor-move" : ""}>{renderCard(id, i)}</div>
            ))}
          </GridLayout>
        </div>
      )}
    </div>
    </div>
  );
}
